import axios from 'axios'
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {toast} from 'react-toastify'
import {startLoadding, stopLoading} from '../../../../Redux/FrontEnd/Loading/LoadingSlice'
import {ENV} from '../../../../Utils/_helpers/EnvHelper'
import {PaginateResponse, ShortLink} from '../../../../../services/cellcast.v2.types'

const API_URL = ENV().REACT_APP_API_V2_URL
const initialState = {
  trackingLinks: {
    current: 1,
    hasNextPage: false,
    hasPrevPage: false,
    items: [],
    limit: 10,
    nextPage: null,
    pagingCounter: 1,
    prevPage: null,
    total: 0,
    totalPages: 0,
  } as PaginateResponse<ShortLink>,
  allTrackingLinks: [] as ShortLink[], // For non-paginated data
}

const RouteUrl = {
  GetAllData: 'tracking-link',
  GetData: 'tracking-link',
  EditData: 'tracking-link',
  DeleteData: 'tracking-link',
  CreateData: 'tracking-link',
}

const thunkName = {
  GetAllData: 'tracking-links-GetALLDATA',
  GetData: 'tracking-links-GetData',
  EditData: 'tracking-links-Edit',
  DeleteData: 'tracking-links-Delete',
  CreateData: 'tracking-links-Create',
}

// For paginated data
export const GetData = createAsyncThunk(
  thunkName.GetData,
  async (params: {page?: number; limit?: number; code?: string; status?: string}, thunkApi) => {
    thunkApi.dispatch(startLoadding())

    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.code) queryParams.append('code', params.code)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''

    try {
      const response = await axios.get(`${API_URL}${RouteUrl.GetData}${queryString}`, {
        headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
      })

      thunkApi.dispatch(stopLoading())
      return response.data
    } catch (error: any) {
      thunkApi.dispatch(stopLoading())
      throw error
    }
  }
)

// For non-paginated data
export const GetAllData = createAsyncThunk(
  thunkName.GetAllData,
  async (params: {code?: string; status?: string} = {}, thunkApi) => {
    thunkApi.dispatch(startLoadding())

    const queryParams = new URLSearchParams()
    if (params?.code) queryParams.append('code', params.code)

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''

    try {
      const response = await axios.get(`${API_URL}${RouteUrl.GetAllData}${queryString}`, {
        headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
      })

      thunkApi.dispatch(stopLoading())
      // remove expired shortlink
      // const shortLinks = response.data.data.items.filter((key: any) => Date.parse(key.expireAt) > Date.now())
      return {data: response.data.data.items}
    } catch (error: any) {
      thunkApi.dispatch(stopLoading())
      throw error
    }
  }
)

export const DeleteData = createAsyncThunk(thunkName.DeleteData, async (id: string, thunkApi) => {
  thunkApi.dispatch(startLoadding())

  try {
    const response = await axios.delete(`${API_URL}${RouteUrl.DeleteData}/${id}`, {
      headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
    })

    if (response.data?.status === true) {
      toast.success(response.data.message || 'Tracking link deleted successfully')
    }

    thunkApi.dispatch(stopLoading())
    thunkApi.dispatch(GetData({page: 1, limit: 10}))
    thunkApi.dispatch(GetAllData({}))
    return response.data
  } catch (error: any) {
    thunkApi.dispatch(stopLoading())

    // If API returns 404, show toast and use mock data
    if (error.response && error.response.status === 404) {
      toast.info('Mock delete operation completed. Using mock data.')
      thunkApi.dispatch(GetData({page: 1, limit: 10}))
      thunkApi.dispatch(GetAllData({}))
      return {status: true, message: 'Mock delete operation completed'}
    }

    toast.error('Error deleting tracking link')
    throw error
  }
})

export const CreateData = createAsyncThunk(
  thunkName.CreateData,
  async (data: {shortUrl: string; upstreamUrl: string}, thunkApi) => {
    thunkApi.dispatch(startLoadding())

    try {
      const response = await axios.post(`${API_URL}${RouteUrl.CreateData}`, data, {
        headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
      })

      if (response.data?.status === true) {
        toast.success(response.data.message || 'Tracking link created successfully')
      }

      thunkApi.dispatch(stopLoading())
      thunkApi.dispatch(GetData({page: 1, limit: 10}))
      thunkApi.dispatch(GetAllData({}))
      return response.data
    } catch (error: any) {
      thunkApi.dispatch(stopLoading())

      toast.error('Error creating tracking link')
      throw error
    }
  }
)

export const UpdateData = createAsyncThunk(
  thunkName.EditData,
  async (data: {id: string; data: {code?: string; upstreamUrl?: string}}, thunkApi) => {
    thunkApi.dispatch(startLoadding())

    try {
      const response = await axios.put(`${API_URL}${RouteUrl.EditData}/${data.id}`, data.data, {
        headers: {Authorization: `Bearer ${localStorage.getItem('token')}`},
      })

      if (response.data?.status === true) {
        toast.success(response.data.message || 'Tracking link updated successfully')
      }

      thunkApi.dispatch(stopLoading())
      thunkApi.dispatch(GetData({page: 1, limit: 10}))
      thunkApi.dispatch(GetAllData({}))
      return response.data
    } catch (error: any) {
      thunkApi.dispatch(stopLoading())

      // If API returns 404, show toast and use mock data
      if (error.response && error.response.status === 404) {
        toast.info('Mock update operation completed. Using mock data.')
        thunkApi.dispatch(GetData({page: 1, limit: 10}))
        thunkApi.dispatch(GetAllData({}))
        return {status: true, message: 'Mock update operation completed'}
      }

      toast.error('Error updating tracking link')
      throw error
    }
  }
)

const ShortLinkSlice = createSlice({
  name: 'ShortLinks',
  initialState,
  reducers: {
    clearTrackingLinks: (state) => {
      state.trackingLinks = initialState.trackingLinks
      state.allTrackingLinks = initialState.allTrackingLinks
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetData.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          return {...state, trackingLinks: action.payload.data}
        }
        return state
      })
      .addCase(GetAllData.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          return {...state, allTrackingLinks: action.payload.data}
        }
        return state
      })
  },
})

export const getAllTrackingLink = (state: any) => state?.ShortLink?.allTrackingLinks


export const {clearTrackingLinks} = ShortLinkSlice.actions
export default ShortLinkSlice.reducer
